import type { MapId } from '../shared/mapData';
import type { MatchAwardSnapshot } from '../shared/protocol';
import type { TeamId } from '../shared/game';

const STORAGE_KEY = 'tribes2.localProfile.v1';

export type LocalPlayerProfile = {
  matchesPlayed: number;
  wins: number;
  captures: number;
  returns: number;
  kills: number;
  deaths: number;
  favoriteMap: MapId | null;
  medalsEarned: Record<string, number>;
};

export type LocalProfileMatchUpdate = {
  matchId: string;
  mapId: MapId;
  winnerTeam: TeamId | null;
  localPlayer: {
    id: string;
    team: TeamId | null;
    kills?: number;
    deaths?: number;
    captures?: number;
    returns?: number;
  };
  awards?: MatchAwardSnapshot[];
};

type StoredProfile = LocalPlayerProfile & {
  mapCounts: Record<string, number>;
  appliedMatchIds: string[];
};

const EMPTY_PROFILE: LocalPlayerProfile = {
  matchesPlayed: 0,
  wins: 0,
  captures: 0,
  returns: 0,
  kills: 0,
  deaths: 0,
  favoriteMap: null,
  medalsEarned: {},
};

function storageAvailable() {
  return typeof window !== 'undefined' && Boolean(window.localStorage);
}

function finiteCount(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
}

function normalizeStoredProfile(raw: unknown): StoredProfile {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const medals = source.medalsEarned && typeof source.medalsEarned === 'object' ? (source.medalsEarned as Record<string, unknown>) : {};
  const mapCounts = source.mapCounts && typeof source.mapCounts === 'object' ? (source.mapCounts as Record<string, unknown>) : {};
  const applied = Array.isArray(source.appliedMatchIds) ? source.appliedMatchIds.filter((id): id is string => typeof id === 'string') : [];

  const normalizedMapCounts: Record<string, number> = Object.fromEntries(
    Object.entries(mapCounts)
      .map(([key, value]) => [key, finiteCount(value)] as const)
      .filter(([, value]) => value > 0),
  );
  const favoriteMap = Object.entries(normalizedMapCounts).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0]?.[0] ?? null;

  return {
    matchesPlayed: finiteCount(source.matchesPlayed),
    wins: finiteCount(source.wins),
    captures: finiteCount(source.captures),
    returns: finiteCount(source.returns),
    kills: finiteCount(source.kills),
    deaths: finiteCount(source.deaths),
    favoriteMap: favoriteMap as MapId | null,
    medalsEarned: Object.fromEntries(
      Object.entries(medals)
        .map(([key, value]) => [key, finiteCount(value)] as const)
        .filter(([, value]) => value > 0),
    ),
    mapCounts: normalizedMapCounts,
    appliedMatchIds: applied.slice(-20),
  };
}

function readStoredProfile(): StoredProfile {
  if (!storageAvailable()) return { ...EMPTY_PROFILE, mapCounts: {}, appliedMatchIds: [] };

  try {
    return normalizeStoredProfile(JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}'));
  } catch {
    return { ...EMPTY_PROFILE, mapCounts: {}, appliedMatchIds: [] };
  }
}

function writeStoredProfile(profile: StoredProfile) {
  if (!storageAvailable()) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

export function loadLocalProfile(): LocalPlayerProfile {
  const { mapCounts: _mapCounts, appliedMatchIds: _appliedMatchIds, ...profile } = readStoredProfile();
  return profile;
}

export function updateLocalProfileFromMatch(update: LocalProfileMatchUpdate): LocalPlayerProfile {
  const stored = readStoredProfile();
  if (!update.matchId || stored.appliedMatchIds.includes(update.matchId)) {
    return loadLocalProfile();
  }

  stored.matchesPlayed += 1;
  stored.wins += update.winnerTeam && update.winnerTeam === update.localPlayer.team ? 1 : 0;
  stored.captures += finiteCount(update.localPlayer.captures);
  stored.returns += finiteCount(update.localPlayer.returns);
  stored.kills += finiteCount(update.localPlayer.kills);
  stored.deaths += finiteCount(update.localPlayer.deaths);
  stored.mapCounts[update.mapId] = (stored.mapCounts[update.mapId] ?? 0) + 1;
  stored.favoriteMap = Object.entries(stored.mapCounts).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0]?.[0] as MapId;

  for (const award of update.awards ?? []) {
    if (award.playerId !== update.localPlayer.id) continue;
    stored.medalsEarned[award.kind] = (stored.medalsEarned[award.kind] ?? 0) + 1;
  }

  stored.appliedMatchIds = [...stored.appliedMatchIds, update.matchId].slice(-20);
  writeStoredProfile(stored);
  return loadLocalProfile();
}

export function resetLocalProfile(): void {
  if (!storageAvailable()) return;
  window.localStorage.removeItem(STORAGE_KEY);
}
